import './SearchBar.scss';

import cn from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import Button from '@/common/components/atoms/Button/Button';
import TextInput from '@/common/components/atoms/TextInput/TextInput';
import { SEARCH_TERM_MIN } from '@/common/constants';
import { useSearchContext } from '@/common/contexts/SearchContext';
import useAutocompleteFocusedItem from '@/common/hooks/search/useAutocompleteFocusedItem';
import useSearchAutocomplete from '@/common/hooks/search/useSearchAutocomplete';
import { ReactComponent as ArrowRightSvg } from '@/ebat/assets/icons/arrow-bold-right.svg';
import { ReactComponent as CloseSvg } from '@/ebat/assets/icons/close.svg';
import { ReactComponent as SearchSvg } from '@/ebat/assets/icons/search.svg';
import { KEY_COMPLETION } from '@/ebat/constants';
import {
  MENU_NAME_SEARCH,
  MENU_TYPE_EXTERNAL,
  useHeaderMenuContext,
} from '@/ebat/contexts/Header/HeaderMenuContext/HeaderMenuContext';

import {
  SearchBarDefaultProps,
  SearchBarPropTypes,
} from './SearchBar.proptypes';
import SearchCompletions from './SearchCompletions/SearchCompletions';
import SearchPlaceholder from './SearchPlaceholder/SearchPlaceholder';

const SearchBar = ({ className, disabled, isLoading }) => {
  const {
    autocompleteTerm,
    localTerm,
    setLocalTerm,
    setSearchParams,
  } = useSearchContext();
  const { isOpen, handleClose, handleOpen } = useHeaderMenuContext();
  const isSearchOpen = isOpen({
    name: MENU_NAME_SEARCH,
    type: MENU_TYPE_EXTERNAL,
  });
  const searchBarRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const { data: completions, isFetching, isSuccess } = useSearchAutocomplete({
    term: autocompleteTerm,
  });
  const { setItemRef, setFocus } = useAutocompleteFocusedItem(completions);
  const isDisabled = {
    loading: isFetching || isLoading,
    disabled: isFetching || isLoading || disabled,
  };
  /**
   * Test if local term is not empty
   * @type {boolean}
   */
  const hasTerm = localTerm.length > 0;

  /**
   * Show autocomplete list (empty or with terms)
   * @type {boolean}
   */
  const showAutoCompletion = hasTerm && isFocused && (isFetching || isSuccess);

  /**
   * Select completion and set new search term
   * @param {event} e - DOM event
   * @param {string} newTerm - New term for search
   * @param {string} key
   */
  const selectCompletion = (e, newTerm, key) => {
    let completion = newTerm;

    if ([KEY_COMPLETION.gammes, KEY_COMPLETION.brands].includes(key)) {
      const newLocalTerm = localTerm.split(' ');
      if (newLocalTerm.length > 0) {
        newLocalTerm[newLocalTerm.length - 1] = newTerm;
        completion = newLocalTerm.join(' ');
      }
    }

    setSearchParams(completion);
    setIsFocused(false);
    handleClose();
  };

  /**
   * Set local and shared terms with search input value
   * @param {event} e - DOM event
   */
  const handleChange = (e) => {
    const { value } = e.target;

    if (value.length >= SEARCH_TERM_MIN) {
      setLocalTerm(value);
      setIsFocused(true);
    } else if (value.length === 0 && localTerm.length > 0) {
      setLocalTerm('');
    }
  };

  /**
   * Open search bar on click in input (desktop)
   * @param {event} e - DOM event
   */
  const handleFocus = () => {
    handleOpen(MENU_NAME_SEARCH, MENU_TYPE_EXTERNAL);
    setIsFocused(true);
  };

  /**
   * Reset input field and close search bar
   * @param {event} e - DOM event
   */
  const handleReset = () => {
    setLocalTerm('');
    setIsFocused(false);
    handleClose();
  };

  /**
   * Set new search term on form submit
   * @param {event} e - DOM event
   */
  const handleSubmit = (e) => {
    const { keyCode, target, type } = e;
    if (type === 'click' || (type === 'keydown' && keyCode === 13)) {
      if (!hasTerm) {
        handleReset(e);
      } else {
        selectCompletion(e, type === 'click' ? localTerm : target.value, '');
        target.blur();
      }
    }
  };

  const handleKeyDown = (e) => {
    const { keyCode } = e;
    if (keyCode === 13) {
      handleSubmit(e);
    } else if (keyCode === 9 || keyCode === 40) {
      e.preventDefault();
      e.stopPropagation();
      setFocus('down');
    }
  };

  const handleAutocompleteItemKeyDown = (e, colIndex, rowIndex) => {
    const { keyCode } = e;
    if (keyCode >= 37 && keyCode <= 40) {
      e.preventDefault();
      e.stopPropagation();
      if (keyCode === 37) {
        setFocus('left', colIndex, rowIndex);
      }
      if (keyCode === 38) {
        setFocus('up', colIndex, rowIndex);
      }
      if (keyCode === 39) {
        setFocus('right', colIndex, rowIndex);
      }
      if (keyCode === 40) {
        setFocus('down', colIndex, rowIndex);
      }
    }
  };

  /**
   * Open/close search suggest if target contain input search
   * @param {event} e - DOM event
   */
  const handleClickOutside = useCallback(
    (e) => {
      if (!searchBarRef.current.contains(e.target)) {
        setIsFocused(false);
        if (isSearchOpen) {
          handleClose();
        }
      }
    },
    [handleClose, isSearchOpen],
  );

  // Add event listener on body to check input search focus
  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

  useEffect(() => {
    if (showAutoCompletion) {
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
    }
  }, [showAutoCompletion]);

  const handleContainerClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isOpen() && !isSearchOpen) {
      handleClose();
    }
  };

  return (
    <div
      role="presentation"
      className={cn('search-bar', className)}
      ref={searchBarRef}
      onClick={handleContainerClick}
      data-qa-id="search-bar"
    >
      <div className="search-bar__container inner-container">
        <div className="search-bar__icon-container">
          <SearchSvg />
        </div>
        <TextInput
          type="search"
          autoComplete="off"
          className="search-bar__field"
          context="search-bar-field"
          onChange={handleChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          value={localTerm}
        />
        <SearchPlaceholder
          className={cn('search-bar__placeholder', {
            hidden: hasTerm || isFocused,
          })}
        />
        <div
          className={cn('search-bar__actions', {
            'search-bar__actions--open': isSearchOpen,
          })}
          data-qa-id="search-bar-actions"
          data-qa-value={cn(
            {
              opened: isSearchOpen,
            },
            {
              closed: !isSearchOpen,
            },
          )}
        >
          <Button
            className="action action--reset"
            context="search-reset"
            onClick={handleReset}
          >
            <CloseSvg className="icon" />
          </Button>
          <Button
            className="action action--submit"
            context="search-validate"
            disabled={isDisabled.disabled || isDisabled.loading}
            onClick={handleSubmit}
          >
            <ArrowRightSvg className="icon" />
          </Button>
        </div>
      </div>
      {showAutoCompletion && (
        <SearchCompletions
          className="search-bar__completions"
          completions={completions}
          disabled={isDisabled.disabled}
          isLoading={isDisabled.loading}
          onItemKeyDown={handleAutocompleteItemKeyDown}
          selectCompletion={selectCompletion}
          setItemRef={setItemRef}
        />
      )}
    </div>
  );
};

SearchBar.defaultProps = {
  ...SearchBarDefaultProps,
};

SearchBar.propTypes = {
  ...SearchBarPropTypes,
};

export default SearchBar;
